// src/features/analytics/WidgetCard.tsx
// One widget tile on the dashboard grid. Fetches its data via QueryData,
// QueryTimeSeries or QueryAggregation depending on widget_type, then renders
// the matching hand-rolled chart.

import { useEffect, useRef, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Card, CardContent, CardHeader, CardTitle } from '@react/components';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { AnchoredMenu, MenuItem } from '@/components/AnchoredMenu';
import { analyticsApi } from '@core/api';
import type { AggregationGroup, ColumnDef, Row, TimeSeries, Widget } from '@core/types/analytics';
import { BarChart, LineAreaChart, MetricTile, PieChart, ResultTable } from './charts';
import { errMessage, isMetricWidget, isPieWidget, isTableWidget, isTimeSeriesWidget, makeEntity, type ScopeIds } from './helpers';

interface WidgetCardProps {
  widget: Widget;
  scope: ScopeIds;
  onEdit: () => void;
  onDeleted: () => void;
}

export function WidgetCard({ widget, scope, onEdit, onDeleted }: WidgetCardProps) {
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Query result state, one of these three depending on widget_type.
  const [metricValue, setMetricValue] = useState<number | null>(null);
  const [series, setSeries] = useState<TimeSeries[]>([]);
  const [groups, setGroups] = useState<AggregationGroup[]>([]);
  const [table, setTable] = useState<{ columns: ColumnDef[]; rows: Row[] } | null>(null);

  useEffect(() => {
    const ds = widget.data_source;
    if (!ds || !ds.product_slug) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const entity = makeEntity('', 'analytics_query', scope);

    const run = async () => {
      if (isTimeSeriesWidget(widget.widget_type)) {
        const res = await analyticsApi.QueryTimeSeries({ entity, data_source: ds });
        setSeries(res.result?.series ?? []);
      } else if (isTableWidget(widget.widget_type)) {
        const res = await analyticsApi.QueryData({ entity, data_source: ds });
        setTable({ columns: res.result?.columns ?? [], rows: res.result?.rows ?? [] });
      } else {
        const res = await analyticsApi.QueryAggregation({ entity, data_source: ds });
        const g = res.result?.groups ?? [];
        setGroups(g);
        if (isMetricWidget(widget.widget_type)) {
          const first = g[0]?.values ? Object.values(g[0].values)[0] : undefined;
          setMetricValue(typeof first === 'number' ? first : null);
        }
      }
    };

    run()
      .catch((e: unknown) => setError(errMessage(e, t('analytics.widgets.queryError'))))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widget.entity?.id, widget.widget_type, JSON.stringify(widget.data_source)]);

  const handleDelete = async () => {
    if (!widget.entity?.id) return;
    setDeleting(true);
    try {
      await analyticsApi.DeleteWidget(widget.entity.id);
      onDeleted();
    } catch (e) {
      setError(errMessage(e, t('analytics.widgets.deleteError')));
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <Card className="flex h-full flex-col">
      <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
        <CardTitle className="text-sm font-medium">{widget.name}</CardTitle>
        <div className="relative">
          <button
            ref={menuTriggerRef}
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label={t('common.moreActions')}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <circle cx="3" cy="8" r="1.3" />
              <circle cx="8" cy="8" r="1.3" />
              <circle cx="13" cy="8" r="1.3" />
            </svg>
          </button>
          <AnchoredMenu
            anchor={menuTriggerRef}
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            align="end"
            ariaLabel={t('common.moreActions')}
            className="w-32 p-1"
          >
            <MenuItem
              label={t('common.edit')}
              onSelect={() => { setMenuOpen(false); onEdit(); }}
            />
            <MenuItem
              label={t('common.delete')}
              destructive
              onSelect={() => { setMenuOpen(false); setConfirmDelete(true); }}
            />
          </AnchoredMenu>
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        {loading && (
          <div className="space-y-2" aria-busy="true">
            <div className="h-24 animate-pulse rounded-md bg-muted" />
          </div>
        )}
        {!loading && error && <p className="text-sm text-destructive">{error}</p>}
        {!loading && !error && !widget.data_source?.product_slug && (
          <p className="text-sm text-muted-foreground">{t('analytics.widgets.noDataSource')}</p>
        )}
        {!loading && !error && widget.data_source?.product_slug && (
          <>
            {isMetricWidget(widget.widget_type) && (
              <MetricTile label={widget.data_source.metrics?.[0]?.alias || widget.name} value={metricValue ?? 0} format={widget.data_source.metrics?.[0]?.format} />
            )}
            {isTimeSeriesWidget(widget.widget_type) && (
              <LineAreaChart series={series} title={widget.name} filled={widget.widget_type === 'WIDGET_TYPE_AREA_CHART'} empty={t('analytics.widgets.noData')} />
            )}
            {isTableWidget(widget.widget_type) && table && (
              <ResultTable columns={table.columns} rows={table.rows} empty={t('analytics.widgets.noData')} />
            )}
            {isPieWidget(widget.widget_type) && (
              <PieChart groups={groups} valueKey="count" donut={widget.widget_type === 'WIDGET_TYPE_DONUT_CHART'} empty={t('analytics.widgets.noData')} />
            )}
            {!isMetricWidget(widget.widget_type) &&
              !isTimeSeriesWidget(widget.widget_type) &&
              !isTableWidget(widget.widget_type) &&
              !isPieWidget(widget.widget_type) && (
                <BarChart groups={groups} valueKey="count" title={widget.name} empty={t('analytics.widgets.noData')} />
              )}
          </>
        )}
      </CardContent>
      <ConfirmDialog
        open={confirmDelete}
        title={t('analytics.widgets.deleteConfirmTitle')}
        description={t('analytics.widgets.deleteConfirmBody', { name: widget.name })}
        destructive
        isConfirming={deleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </Card>
  );
}
